import React from "react";

import {
  EmployeePercentage,
  DependentPercentage,
  EmployeeFixedAmount,
  DependentFixedAmount,
  TypeOfContribution,
  ContributionMethod,
  ProbationaryPeriod,
} from "EmployerApplication/Components/FinalFormQuestions";
import { hasMultipleMedicalPlansOffered } from "EmployerApplication/Selectors";
import _ from "lodash";
import { useFormState } from "react-final-form";
import { connect } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import AppearingElement from "Common/AppearingElement";
import { TIERED_CONTRIBUTIONS_SORTING_ORDER, TIERED_CONTRIBUTION_ROLES, CONTRIBUTION_TYPES, MIN_TIERED_CONTRIBUTIONS } from "Common/Constants";
import { Row, Col } from "Common/Grid";
import { Div } from "Common/HTML";

import PayrollContributionSection from "./PayrollContributionSection";
import PayrollContributionTable from "./PayrollContributionTable";
import { contributionIsComplete } from "./TieredContributions/Selectors";
import TieredContributionsForm from "./TieredContributions/TieredContributionsForm";

const PERCENT_CONTRIBUTION = CONTRIBUTION_TYPES.percentage;
const PERCENT_OF_BASE_PLAN_CONTRIBUTION = CONTRIBUTION_TYPES.percent_of_base_plan;
const DOLLAR_CONTRIBUTION = CONTRIBUTION_TYPES.fixed;

const NonTieredContributionSection = ({ contributionType, multipleMedicalPlansOffered }) => (
  <Div>
    <Row>
      <TypeOfContribution multipleMedicalPlansOffered={multipleMedicalPlansOffered} md={8} />
    </Row>
    <AppearingElement show={contributionType === PERCENT_CONTRIBUTION || contributionType === PERCENT_OF_BASE_PLAN_CONTRIBUTION}>
      <Row>
        <EmployeePercentage md={8} />
      </Row>
      <Row>
        <DependentPercentage md={8} />
      </Row>
    </AppearingElement>
    <AppearingElement show={contributionType === DOLLAR_CONTRIBUTION}>
      <Row>
        <EmployeeFixedAmount md={8} />
      </Row>
      <Row>
        <DependentFixedAmount md={8} />
      </Row>
    </AppearingElement>
  </Div>
);

const getDefaultContribution = (contributions = []) => {
  let defaultContribution;
  for (const role of TIERED_CONTRIBUTIONS_SORTING_ORDER) {
    defaultContribution = contributions.find((contribution) => contribution.role === role);

    if (defaultContribution !== undefined) {
      return defaultContribution;
    }
  }
  return {};
};

// EmployeeDetailsBody inside PayrollContributionSection uses ReduxForm to power some very complex logic, so
// can't do anything with that yet.
export const ContributionAmountSectionDumb = ({ multipleMedicalPlansOffered, isRenewal, payrollContributionSectionProps }) => {
  const formValues = useFormState().values;
  const contributionMethod = formValues.company_application?.contribution_method;
  const defaultContribution = getDefaultContribution(formValues?.tiered_contributions);
  const defaultContributionRole = TIERED_CONTRIBUTION_ROLES[defaultContribution.role];

  const contribution = {
    employeePercentage: formValues.company_contribution?.employee_percentage,
    dependentPercentage: formValues.company_contribution?.dependent_percentage,
    employeeFixedAmount: formValues.company_contribution?.employee_fixed_amount,
    dependentFixedAmount: formValues.company_contribution?.dependent_fixed_amount,
  };

  const { employeeProfile, allEmployeeCosts, typeOfContribution } = payrollContributionSectionProps;

  return (
    <>
      <Div className="ContributionAmountSection">
        <Row>
          <ContributionMethod
            onBlur={() => {
              if (contributionMethod === "tiered") {
                AnalyticsTracking.trackEvent("started_tiered_contributions_form", {});
              }
            }}
          />
        </Row>
        <AppearingElement show={contributionMethod === "tiered"}>
          <TieredContributionsForm
            contributionType={formValues.company_application?.type_of_contribution}
            multipleMedicalPlansOffered={multipleMedicalPlansOffered}
          />
        </AppearingElement>

        {/* This question exists on the CompanyInformationPage which is skipped by renewal groups */}
        {isRenewal && (
          <Row>
            <ProbationaryPeriod />
          </Row>
        )}

        <AppearingElement show={contributionMethod === "nonTiered"}>
          <NonTieredContributionSection
            contributionType={formValues.company_application?.type_of_contribution}
            multipleMedicalPlansOffered={multipleMedicalPlansOffered}
          />
        </AppearingElement>
      </Div>
      {isRenewal ? (
        <Row>
          <Col sm={7}>
            <AppearingElement show={[PERCENT_CONTRIBUTION, DOLLAR_CONTRIBUTION].includes(typeOfContribution)}>
              <Row pl={4}>
                <PayrollContributionTable
                  employeeProfiles={employeeProfile}
                  contributionType={typeOfContribution}
                  employeesCosts={allEmployeeCosts}
                  contributions={
                    contributionMethod === "tiered"
                      ? formValues.tiered_contributions.filter((c) => contributionIsComplete(c, typeOfContribution))
                      : [contribution]
                  }
                  showMultiplePlansDisclaimer={multipleMedicalPlansOffered}
                />
              </Row>
            </AppearingElement>
          </Col>
        </Row>
      ) : (
        <Row mt={3}>
          <Col lg={9}>
            <PayrollContributionSection
              popoverInfoText={
                contributionMethod === "tiered"
                  ? `The Company Share and Payroll Deduction reflect the ${defaultContributionRole} tier only.
                You can assign employees to tiers later on in the application process.`
                  : ""
              }
            />
          </Col>
        </Row>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  multipleMedicalPlansOffered: hasMultipleMedicalPlansOffered(state),
});

const withRedux = connect(mapStateToProps);
const ContributionAmountSection = withRedux(ContributionAmountSectionDumb);

export default ContributionAmountSection;
